/**
 * 형제 문서 칩 라벨의 **중복 해소** — 결정 2026-09-10 「분할 묶음에서 «행의 소유»를 무엇으로
 * 볼 것인가」의 귀결.
 *
 * 잠금이 «행» 축이 되면서 **같은 `splitGroupKey`(따라서 같은 `splitGroupLabel`) 문서가 둘
 * 생길 수 있다** — 공종 A의 5행 중 1행만 담은 문서가 있으면 남은 4행으로 A 문서를 또 만드는
 * 것이 정상이고, 그것이 그 전에 갇혀 있던 행의 출구다. 그러면 형제 바에서 두 칩의 **글자가
 * 완전히 같아져** 사용자가 둘을 구분할 수 없다(제목 중복보다 이쪽이 실사용에 먼저 닿는다).
 *
 * **중복일 때만 순번을 붙인다.** 멀쩡한 화면(전부 다른 그룹)에 번호가 늘어나면 안 된다 —
 * 분할의 압도적 다수가 그 경우다.
 *
 * **왜 여기 사는가**: 같은 규칙이 필요한 자리가 둘이고 재료가 다르다 —
 * 인증 화면 `SiblingReportChips`(`Report` 전체를 먹는다)와 무인증 담당자 화면
 * `SplitSiblingBar`(서버가 준 5필드 투영만 먹는다). 그래서 **기본 라벨 파생은 각 호스트가**
 * 하고(폴백 사다리가 정당하게 다르다: 앞은 `title`로, 뒤는 `splitGroupKey`→`N번째 문서`로
 * degrade), **중복 해소 규칙만** 공유한다. 두 곳이 갈리면 인증 화면과 담당자 화면이 같은
 * 형제를 다르게 부른다.
 *
 * ⚠️ 서버 계약도 `splitGroupLabel`도 건드리지 않는다 — 이것은 **표시 층의 파생**이다.
 * 저장된 라벨에 순번을 박으면 문서가 지워졌을 때 남은 문서의 번호가 틀어진다(순번은 "지금
 * 이 목록 안에서 몇 번째 동명이인인가"이지 문서의 속성이 아니다).
 */

/**
 * 목록 순서(생성순)를 유지한 채, **두 번 이상 나타나는 라벨에만** `(1)`·`(2)`…를 붙인다.
 *
 * - 한 번만 나타나는 라벨은 **그대로** 둔다.
 * - 번호는 동명 그룹 안에서 1부터, 주어진 배열 순서대로 매긴다(호출부가 생성순으로 준다).
 * - 비교는 받은 문자열 그대로 한다 — 다듬기(trim)·폴백은 호출부의 책임이고, 여기서 한 번 더
 *   손대면 호스트가 고른 표시 문자열과 판정 대상이 갈린다.
 *
 * @param labels 각 형제의 기본 표시 라벨(호스트가 자기 폴백 사다리로 파생한 것)
 * @returns 같은 길이·같은 순서의 표시용 라벨 배열
 */
export function disambiguateSiblingLabels(labels: readonly string[]): string[] {
  const total = new Map<string, number>();
  for (const label of labels) {
    total.set(label, (total.get(label) ?? 0) + 1);
  }

  const seen = new Map<string, number>();
  return labels.map((label) => {
    // 유일하면 손대지 않는다 — 이것이 "중복일 때만"의 전부다.
    if ((total.get(label) ?? 0) < 2) return label;
    const ordinal = (seen.get(label) ?? 0) + 1;
    seen.set(label, ordinal);
    return `${label} (${ordinal})`;
  });
}
